import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { byDecimal } from '@/components/data/byDecimal'
import { RecordId } from '@/components/data/DataTable'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { SuppressedValue } from '@/components/data/SuppressedValue'
import {
  toHiddenColumns,
  toVisibilityState,
  type ListViewControls,
  type ListViewState,
} from '@/components/data/useViewState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import { Tag } from '@/components/ui/Tag'
import { useSession } from '@/features/auth/api/useSession'
import { useIssuedDocuments } from '@/features/finance/api/useFinance'
import { mayOpenArchivedKind } from '@/features/finance/documentAccess'
import { ISSUED_LOCKED_COLUMNS } from '@/features/finance/issuedDocumentColumns'
import { RenderedDocument } from '@/features/finance/components/DocumentPreview'
import { TableEmptyState } from '@/components/data/TableEmptyState'
import { useFormat } from '@/i18n/useFormat'
import { formatNumber } from '@/lib/format'
import type { IssuedDocument, Money } from '@/types/domain'

/* The company-wide register of ISSUED paper — the Documents tab with no case
 * chosen (N-30).
 *
 * The tab used to be empty until a case was picked, because every read of paper
 * was nested under `/cases/:id`. This one is `GET /finance/issued-documents`:
 * issued invoices and debit notes of every case, newest first, with what has
 * been paid and what is left. It is READ-ONLY and DRAFT-FREE — a draft raises
 * no receivable (FR-62) — so the editors, the render and the issue stay on the
 * case, and choosing a case in the picker above is the way into them.
 *
 * ONE ROW MIXES CURRENCIES, on purpose and visibly. `total` is the document's
 * own currency; `paid` and `outstanding` are booked USD, because that is what an
 * allocation settles in (AD-11). So every figure prints with ITS OWN currency
 * beside it, and nothing here adds, subtracts or converts: the three numbers
 * are the server's strings, and `outstanding` is signed as served.
 *
 * Its own view-state slug, not `finance.documents`: the columns are not the
 * case table's, and a saved sort or hidden set from one would name columns the
 * other does not have (the argument the case table's own slug makes). */

const column = createColumnHelper<IssuedDocument>()

/** A served amount with its own currency beside it. */
function MoneyCell({ money }: { money: Money }) {
  return (
    <>
      {formatNumber(money.amount, 2)}
      <span className="text-text-muted ml-1 text-[12px] font-normal">{money.currency}</span>
    </>
  )
}

export function IssuedDocumentRows({
  range,
  kindFilter,
  viewState,
  controls,
}: {
  range: string
  kindFilter: readonly string[]
  viewState: ListViewState
  controls: ListViewControls
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const register = useIssuedDocuments(range)
  const [openId, setOpenId] = useState<string | null>(null)

  const items = register.data?.items
  const rows = useMemo(() => {
    const all = items ?? []
    if (kindFilter.length === 0) return all
    return all.filter((document) => kindFilter.includes(document.kind))
  }, [items, kindFilter])

  const columnVisibility = useMemo(
    () =>
      toVisibilityState(viewState.hiddenColumns.filter((id) => !ISSUED_LOCKED_COLUMNS.includes(id))),
    [viewState.hiddenColumns],
  )

  const columns = useMemo(
    () => [
      column.accessor('number', {
        id: 'number',
        header: t('common.fields.number'),
        size: 172,
        cell: (info) => (
          <span className="flex items-center gap-2">
            <RecordId>{info.getValue()}</RecordId>
            <Tag>{t(`documentKind.${info.row.original.kind}`)}</Tag>
          </span>
        ),
      }),
      column.accessor('counterpartyName', {
        id: 'counterparty',
        header: t('finance.documents.raise.counterparty'),
        // AD-6: withheld without `counterparty.read`; the marker is rendered, not
        // a blank that reads as a field nobody filled in.
        cell: (info) => <SuppressedValue value={info.getValue()} />,
      }),
      column.accessor('caseNumber', {
        id: 'case',
        header: t('finance.documents.register.caseColumn'),
        size: 120,
        cell: (info) => (
          /* A link inside a clickable row: it must not also open the sheet. */
          <Link
            to={`/cases/${info.row.original.caseId}`}
            onClick={(event) => event.stopPropagation()}
            className="text-text-link hover:text-text-link-hover"
          >
            {info.getValue()}
          </Link>
        ),
      }),
      column.accessor('entityName', {
        id: 'entity',
        header: t('finance.documents.register.entityColumn'),
        size: 160,
        cell: (info) => <span className="truncate">{info.getValue()}</span>,
      }),
      column.accessor('documentDate', {
        header: t('finance.documents.raise.noteDate'),
        size: 120,
        cell: (info) => format.date(info.getValue()),
      }),
      column.accessor((row) => row.total.amount, {
        id: 'total',
        header: t('common.fields.total'),
        size: 148,
        meta: { numeric: true },
        sortingFn: byDecimal<IssuedDocument>((row) => row.total.amount),
        cell: (info) => <MoneyCell money={info.row.original.total} />,
      }),
      column.accessor((row) => row.paid.amount, {
        id: 'paid',
        header: t('finance.documents.register.paidColumn'),
        size: 148,
        meta: { numeric: true },
        sortingFn: byDecimal<IssuedDocument>((row) => row.paid.amount),
        cell: (info) => <MoneyCell money={info.row.original.paid} />,
      }),
      column.accessor((row) => row.outstanding.amount, {
        id: 'outstanding',
        header: t('finance.documents.register.outstandingColumn'),
        size: 156,
        meta: { numeric: true },
        sortingFn: byDecimal<IssuedDocument>((row) => row.outstanding.amount),
        cell: (info) => <MoneyCell money={info.row.original.outstanding} />,
      }),
    ],
    [t, format],
  )

  if (register.isPending) {
    return <Skeleton className="h-48 rounded-[var(--radius-DEFAULT)]" />
  }
  if (register.isError) {
    return <ErrorState error={register.error} onRetry={() => void register.refetch()} />
  }

  const total = register.data.total
  const opened = (items ?? []).find((document) => document.id === openId)

  return (
    <>
      <RecordList
        label={t('finance.documents.title')}
        data={rows}
        columns={columns}
        getRowId={(document) => document.id}
        onRowClick={(document) => setOpenId(document.id)}
        renderCard={(document) => (
          <RecordCard
            id={document.number}
            status={<Tag>{t(`documentKind.${document.kind}`)}</Tag>}
            primary={<SuppressedValue value={document.counterpartyName} />}
            secondary={[document.caseNumber, format.date(document.documentDate)].join(' · ')}
            value={`${formatNumber(document.total.amount, 2)} ${document.total.currency}`}
            valueLabel={t('common.fields.total')}
          />
        )}
        sorting={viewState.sorting}
        onSortingChange={controls.setSorting}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={(next) => controls.setHiddenColumns(toHiddenColumns(next))}
        /* The period is a filter too: an empty register under "1M" is "nothing
           in this period", and the way back is the period control above. The
           kind facet is the one this table can clear itself. */
        filtered={kindFilter.length > 0 && (items ?? []).length > 0}
        onClearFilters={controls.clearFilters}
        empty={
          <TableEmptyState
            filtered={false}
            title={t('finance.documents.register.emptyTitle')}
            description={t('finance.documents.register.emptyDescription')}
          />
        }
      />

      {/* The server caps the list at its newest rows and says how many matched;
          a register that quietly stopped at 500 would read as the whole. */}
      {total > (items ?? []).length ? (
        <p className="text-text-muted px-4 py-3 text-[12.5px] font-light">
          {t('finance.documents.register.capped', { shown: (items ?? []).length, total })}
        </p>
      ) : null}

      {opened ? <IssuedDocumentSheet document={opened} onClose={() => setOpenId(null)} /> : null}
    </>
  )
}

/** One issued document, opened: its archived PDF and the ways into its case.
 *
 *  The file is opened through `documentId` and the existing preview path, only
 *  for a reader whose capabilities open that kind (`mayOpenArchivedKind`), and
 *  QUIETLY otherwise: no `documentId` (nothing was rendered), no capability, or
 *  a 403/404 from the archive (a role that reads the register but sits outside
 *  the case's scope) each say so in one muted sentence — no toast, no Retry
 *  that cannot succeed. */
function IssuedDocumentSheet({
  document,
  onClose,
}: {
  document: IssuedDocument
  onClose: () => void
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const { data: session } = useSession()

  const mayOpen = mayOpenArchivedKind(session, document.kind)

  return (
    <Sheet
      open
      onClose={onClose}
      size="wide"
      title={document.number}
      footer={
        <Button variant="secondary" size="lg" className="w-full" onClick={onClose}>
          {t('common.actions.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <dl className="text-text-body grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
          <dt className="text-text-muted">{t('finance.documents.raise.counterparty')}</dt>
          <dd>
            <SuppressedValue value={document.counterpartyName} />
          </dd>
          <dt className="text-text-muted">{t('finance.documents.register.entityColumn')}</dt>
          <dd>{document.entityName}</dd>
          <dt className="text-text-muted">{t('finance.documents.raise.noteDate')}</dt>
          <dd>{format.date(document.documentDate)}</dd>
          <dt className="text-text-muted">{t('common.fields.total')}</dt>
          <dd className="asa-num">
            <MoneyCell money={document.total} />
          </dd>
          <dt className="text-text-muted">{t('finance.documents.register.paidColumn')}</dt>
          <dd className="asa-num">
            <MoneyCell money={document.paid} />
          </dd>
          <dt className="text-text-muted">
            {t('finance.documents.register.outstandingColumn')}
          </dt>
          <dd className="asa-num">
            <MoneyCell money={document.outstanding} />
          </dd>
        </dl>

        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <Link
            to={`/cases/${document.caseId}`}
            className="text-text-link hover:text-text-link-hover w-fit text-[12.5px]"
          >
            {t('finance.documents.register.openCase', { number: document.caseNumber })}
          </Link>
          {document.kind === 'invoice' ? (
            <Link
              to={`/cases/${document.caseId}/invoices/${document.id}`}
              className="text-text-link hover:text-text-link-hover w-fit text-[12.5px]"
            >
              {t('cases.documents.openInvoice')}
            </Link>
          ) : null}
        </div>

        <div className="border-line-hair border-t pt-3">
          {document.documentId === undefined ? (
            <p className="text-text-muted text-[12.5px] font-light">
              {t('finance.documents.register.noFile')}
            </p>
          ) : !mayOpen ? (
            <p className="text-text-muted text-[12.5px] font-light">
              {t('finance.documents.register.fileUnavailable')}
            </p>
          ) : (
            <RenderedDocument
              key={document.documentId}
              documentId={document.documentId}
              unavailable={t('finance.documents.register.fileUnavailable')}
            />
          )}
        </div>
      </div>
    </Sheet>
  )
}
